import React from 'react';
import { Page, Text, View, Document, StyleSheet, Font, Image } from '@react-pdf/renderer';

// --- Registrace lokálního fontu ---
Font.register({
  family: 'CustomFont',
  src: '/fonts/LiberationSans-Regular.ttf',
});

// --- Styling ---
const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    backgroundColor: '#FFFFFF',
    padding: 25,
    fontFamily: 'CustomFont',
    fontSize: 9,
  },
  headerContainer: {
    width: '100%',
    marginBottom: 12, // Zvýšené odsazení pod logem
    flexDirection: 'row',
    alignItems: 'center',
  },
  logo: {
    width: 40,
    objectFit: 'contain',
    marginRight: 8,
  },
  companyName: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'left',
  },
  // Kontejner pro název
  titleContainer: {
    width: '100%',
    marginTop: 8,  // Přidáno odsazení od loga
    marginBottom: 10,
  },
  title: {
    fontSize: 14,
    fontWeight: 'bold',
    textAlign: 'left',
  },
  headerInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 12,
    fontSize: 10,
  },
  section: {
    marginBottom: 10,
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: 'bold',
    marginBottom: 5,
    backgroundColor: '#f0f0f0',
    padding: 3,
    borderBottomWidth: 1,
    borderBottomColor: '#cccccc',
  },
  itemRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 2,
    padding: 4,
    borderBottomWidth: 0.5,
    borderBottomColor: '#eeeeee',
  },
  itemDescriptionContainer: {
    width: '70%',
  },
  itemDetails: {
    fontSize: 8,
    color: '#555555',
    marginTop: 1,
    paddingLeft: 8,
  },
  itemDescriptionExtraText: {
    color: '#333333',
  },
  itemValueContainer: {
    width: '30%',
    textAlign: 'right',
  },
  itemValue: {
    textAlign: 'right',
  },
  textBold: {
    fontWeight: 'bold',
  },
  doplatekValue: {
    fontSize: 10,
    fontWeight: 'bold',
    textAlign: 'right',
  },
  footer: {
    position: 'absolute',
    bottom: 10,
    left: 25,
    right: 25,
    textAlign: 'center',
    fontSize: 7,
    color: 'grey',
  },
});

// --- Helper Functions ---
const formatNumber = (amount, decimals = 2) => {
  if (amount == null) return '';
  return amount.toLocaleString('cs-CZ', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
};

// --- PDF Document Component ---
const PayslipPDF = ({ data, userName }) => {
  const payslip = data && data.length > 0 ? data[0] : null;

  if (!payslip || !payslip.listek) {
    return (
      <Document>
        <Page size="A4" style={styles.page}>
          <Text>Chyba: Data pro výplatní lístek nejsou k dispozici.</Text>
        </Page>
      </Document>
    );
  }

  const { listek, mesic, rok, osci } = payslip;
  const now = new Date();
  const formattedDate = now.toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' }) +
    ' ' + now.toLocaleTimeString('cs-CZ');

  // --- renderItemValue ---
  const renderItemValue = (item) => {
    if (item.castka == null) return null;
    const isBold = item.typPolozky === 'bold';
    const isDoplatek = item.typSekce === 'dopla';
    const decimals = item.typPolozky === 'DD' ? 2 : (item.castka % 1 !== 0 ? 2 : 0);
    let formattedValue = formatNumber(item.castka, decimals);
    if (item.mj) formattedValue += ` ${item.mj}`;
    const valueStyle = [
      isDoplatek ? styles.doplatekValue : styles.itemValue,
      isBold && styles.textBold
    ];
    if (!isDoplatek) valueStyle.push({ fontSize: styles.page.fontSize });
    return <Text style={valueStyle}>{formattedValue}</Text>;
  };

  return (
    <Document title={`Výplatní lístek ${osci} - ${String(mesic).padStart(2, '0')}/${rok}`}>  
      <Page size="A4" style={styles.page}>
        {/* Header with logo and company name */}
        <View style={styles.headerContainer} fixed>
          <Image style={styles.logo} src="/images/logo.png" />
          <Text style={styles.companyName}>Povodí Vltavy, státní podnik</Text>
        </View>

        {/* Title on its own line, left-aligned */}
        <View style={styles.titleContainer}>
          <Text style={styles.title}>VÝPLATNÍ LÍSTEK</Text>
        </View>

        {/* Info row */}
        <View style={styles.headerInfo}>
          <Text>Období: {String(mesic).padStart(2, '0')}/{rok}</Text>
          <Text>Osobní číslo: {osci}</Text>
          <Text>Jméno: {userName}</Text>
        </View>

        {/* Sections and items */}
        {listek.map((section, sIdx) => (
          <View key={sIdx} style={styles.section}>
            <Text style={styles.sectionTitle}>{section.sekce}</Text>
            {section.polozka?.map((item, iIdx) => (
              <View key={`${sIdx}-${iIdx}`} style={styles.itemRow}>
                <View style={styles.itemDescriptionContainer}>
                  <Text style={[styles.itemDescription, item.typPolozky === 'bold' && styles.textBold]}>
                    {item.popis}{item.txt && (<Text style={styles.itemDescriptionExtraText}>{' (' + item.txt + ')'}</Text>)}
                  </Text>
                  {(item.hodiny != null || item.zaklad != null || (item.od && item.do)) && (
                    <View>
                      {item.hodiny != null && <Text style={styles.itemDetails}>Hodiny: {formatNumber(item.hodiny, 2)}</Text>}
                      {item.zaklad != null && <Text style={styles.itemDetails}>Základ: {formatNumber(item.zaklad, 0)}</Text>}
                      {item.od && item.do && <Text style={styles.itemDetails}>Období: {item.od} - {item.do}</Text>}
                    </View>
                  )}
                </View>
                <View style={styles.itemValueContainer}>
                  {renderItemValue({ ...item, typSekce: section.typSekce })}
                </View>
              </View>
            ))}
          </View>
        ))}

        {/* Footer with HRIS info and timestamp */}
        <Text style={styles.footer} fixed>
          {`Zpracoval HRIS ver. 2025.05 (c)1992-2025 dne ${formattedDate}`}
          <Text render={({ pageNumber, totalPages }) =>
            totalPages > 1 ? ` Strana ${pageNumber} / ${totalPages}` : ''
          } />
        </Text>
      </Page>
    </Document>
  );
};

export default PayslipPDF;